import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { ApiError } from "@/api/errors";
import type { components, operations } from "@/api/schema";

export type ActivityEvent = components["schemas"]["ActivityEvent"];
export type ActivityPageCounts = components["schemas"]["ActivityPageCounts"];
export type ActivitySource = NonNullable<
  NonNullable<operations["getOrderActivity"]["parameters"]["query"]>["source"]
>;

function activityKey(orderId: string, source: ActivitySource | undefined) {
  return ["order", orderId, "activity", source ?? "all"] as const;
}

/**
 * GET /v1/orders/{id}/activity — the merged per-order feed (MO plan 4 §7/§8.2),
 * cursor-paginated newest-first. `source` narrows to one origin; omit for all.
 */
export function useActivity(orderId: string, source?: ActivitySource) {
  return useInfiniteQuery({
    queryKey: activityKey(orderId, source),
    enabled: !!orderId,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET("/v1/orders/{id}/activity/feed", {
        params: {
          path: { id: orderId },
          query: {
            ...(source ? { source } : {}),
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        },
      });
      if (error) throw error;
      return data;
    },
    getNextPageParam: (lastPage) => lastPage?.next_cursor || undefined,
  });
}

/**
 * POST /v1/orders/{id}/notes — appends an "order.note" canonical audit
 * event. On success, invalidates every activity page for this order (across
 * all source filters) so the new note reappears at the top of the feed.
 */
export function useAddNote(
  orderId: string,
): UseMutationResult<{ audit_event_id: number }, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<{ audit_event_id: number }, ApiError, string>({
    meta: { silentError: true },
    mutationFn: async (text: string) => {
      const { data, error } = await apiClient.POST("/v1/orders/{id}/notes", {
        params: { path: { id: orderId } },
        body: { text },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["order", orderId, "activity"] });
    },
  });
}

/**
 * DELETE /v1/orders/{id}/notes/{audit_event_id} — soft-deletes a note.
 * Backend only allows this for the note's own author within 5 minutes of
 * posting; the UI mirrors that window in ActivityRow before ever offering
 * the control (see OrderActivity's canDeleteNote).
 */
export function useDeleteNote(orderId: string): UseMutationResult<void, ApiError, number> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, number>({
    meta: { silentError: true },
    mutationFn: async (auditEventId: number) => {
      const { error } = await apiClient.DELETE("/v1/orders/{id}/notes/{audit_event_id}", {
        params: { path: { id: orderId, audit_event_id: auditEventId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["order", orderId, "activity"] });
    },
  });
}

/**
 * GET /v1/orders/{id}/raw — the raw marketplace payload behind an order.
 * `enabled` gates the request until the caller actually opens the drawer
 * (an activity row's `▸`), matching PayloadDrawer's on-demand fetch.
 */
export function useRawPayload(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["order", orderId, "raw"],
    enabled: enabled && !!orderId,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/orders/{id}/raw/payload", {
        params: { path: { id: orderId } },
      });
      if (error) throw error;
      return data;
    },
  });
}
